/**
 * Precision Planning — Land Expansion Modelling  (mvp2_agrotech_precision)
 *
 * Site-suitability analysis for new land acquisitions.  Scores candidate
 * sites (satellite boundaries not yet assigned to any crop) against
 * agronomic criteria derived from satellite indices, computes a simple
 * NPV proxy, and presents a ranked comparison table.
 *
 * A candidate boundary is identified by having no crop_type in its metadata
 * (or having crop_type = 'Other').  Any boundary can also be manually added
 * to the candidate list via the UI.
 *
 * Data sources (all existing endpoints):
 *  • GET /api/satellite/boundaries          — all defined boundaries
 *  • GET /api/satellite-imagery/:id/records — index data per boundary
 *
 * NPV proxy formula
 * ─────────────────
 * Napkin NPV over 10 years:
 *   revenuePY = areaHa × yieldIndexFactor × pricePerKg × kgPerHa
 *   npvProxy   = revenuePY × (1 − 1/(1+r)^n) / r  −  capex
 *
 * All cost/price/yield coefficients are editable by the user in the form.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Expand, RefreshCw, ZapOff,
  Calculator, Info, ChevronDown, ChevronUp,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import {
  getBoundaries, getBatchedImagery, invalidateCache,
  type CachedField,
} from '../../services/precisionDataCache';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ─────────────────────────────────────────────────────────────────────

type Field = CachedField;

interface SiteIndices {
  field: Field;
  ndviMean: number | null;
  ndreMean: number | null;
  ndwiMean: number | null;
  suitabilityScore: number;    // 0–100
  suitabilityTier: 'excellent' | 'good' | 'fair' | 'poor';
  expanded: boolean;
}

interface SiteScore extends SiteIndices {
  npvProxy: number | null;
  paybackYears: number | null;
}

interface ModelParams {
  targetCrop: string;
  pricePerKg: number;
  kgPerHa: number;
  capexPerHa: number;
  opexPerHa: number;
  discountRate: number;
  horizonYears: number;
}

// ─── NPV calculation ───────────────────────────────────────────────────────────

function calcNpv(
  areaHa: number,
  ndviMean: number | null,
  params: ModelParams,
): { npv: number | null; paybackYears: number | null } {
  if (!areaHa || areaHa <= 0) return { npv: null, paybackYears: null };
  const yieldFactor = ndviMean ? Math.min(1, ndviMean / 0.7) : 0.6;
  const revenuePY = areaHa * params.kgPerHa * yieldFactor * params.pricePerKg;
  const opexPY    = areaHa * params.opexPerHa;
  const capex     = areaHa * params.capexPerHa;
  const n         = params.horizonYears;
  const r         = params.discountRate / 100;
  const annuity   = r > 0 ? (1 - Math.pow(1 + r, -n)) / r : n;
  const npv       = +(( (revenuePY - opexPY) * annuity) - capex).toFixed(0);
  const payback   = revenuePY > opexPY ? +(capex / (revenuePY - opexPY)).toFixed(1) : null;
  return { npv, paybackYears: payback };
}

// ─── Suitability from indices ──────────────────────────────────────────────────

function computeSuitability(ndvi: number | null, ndre: number | null, ndwi: number | null): { score: number; tier: SiteScore['suitabilityTier'] } {
  const ndviScore = ndvi !== null ? Math.min(100, (ndvi / 0.8) * 100) : 50;
  const ndreScore = ndre !== null ? Math.min(100, (ndre / 0.2) * 100) : 50;
  const ndwiScore = ndwi !== null ? Math.min(100, ((ndwi + 0.2) / 0.7) * 100) : 50;
  const score = +(ndviScore * 0.5 + ndreScore * 0.3 + ndwiScore * 0.2).toFixed(1);
  const tier: SiteScore['suitabilityTier'] =
    score >= 75 ? 'excellent' : score >= 55 ? 'good' : score >= 35 ? 'fair' : 'poor';
  return { score: Math.min(100, Math.max(0, score)), tier };
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const TIER_STYLE: Record<SiteScore['suitabilityTier'], string> = {
  excellent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  good:      'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  fair:      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  poor:      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

function SiteCard({ site, onToggle, params }: {
  site: SiteScore;
  onToggle: () => void;
  params: ModelParams;
}) {
  const { uom } = useOrgSettings();
  const { npv, paybackYears } = calcNpv(site.field.area_hectares ?? 1, site.ndviMean, params);

  return (
    <Card>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-base font-bold text-gray-900 dark:text-white">{site.field.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {site.field.area_hectares?.toFixed(1) ?? '—'} {uom.area_unit_label}
              {site.field.metadata?.crop_type && ` · Current: ${site.field.metadata.crop_type}`}
            </p>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${TIER_STYLE[site.suitabilityTier]}`}>
            {site.suitabilityTier.toUpperCase()} — {site.suitabilityScore}/100
          </span>
        </div>

        {/* Suitability bar */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Site suitability score</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-2 rounded-full ${site.suitabilityTier === 'excellent' ? 'bg-green-500' : site.suitabilityTier === 'good' ? 'bg-teal-500' : site.suitabilityTier === 'fair' ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${site.suitabilityScore}%` }}
              />
            </div>
            <span className="text-xs font-bold text-gray-700 dark:text-gray-300 w-8 text-right">{site.suitabilityScore}</span>
          </div>
        </div>

        {/* NPV row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2 col-span-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">10-Year NPV Proxy ({params.targetCrop})</p>
            <p className={`text-sm font-bold ${npv !== null && npv >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {npv !== null ? `${uom.currency_symbol}${Math.abs(npv).toLocaleString()} ${npv < 0 ? '(loss)' : ''}` : '—'}
            </p>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Payback</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{paybackYears !== null ? `${paybackYears} yr` : '—'}</p>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">NDVI mean</p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{site.ndviMean?.toFixed(3) ?? '—'}</p>
          </div>
        </div>

        {/* Expand */}
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
          <button type="button" onClick={onToggle}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1">
            {site.expanded ? <><ChevronUp className="w-3.5 h-3.5" />Hide index detail</> : <><ChevronDown className="w-3.5 h-3.5" />Show index detail</>}
          </button>
          {site.expanded && (
            <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-gray-600 dark:text-gray-400">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
                <p className="text-[10px] text-gray-400 mb-0.5">NDVI (vigour)</p>
                <p className="font-bold text-gray-800 dark:text-white">{site.ndviMean?.toFixed(4) ?? '—'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
                <p className="text-[10px] text-gray-400 mb-0.5">NDRE (nitrogen)</p>
                <p className="font-bold text-gray-800 dark:text-white">{site.ndreMean?.toFixed(4) ?? '—'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
                <p className="text-[10px] text-gray-400 mb-0.5">NDWI (moisture)</p>
                <p className="font-bold text-gray-800 dark:text-white">{site.ndwiMean?.toFixed(4) ?? '—'}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Model params panel ────────────────────────────────────────────────────────

function ParamsPanel({
  params,
  onChange,
}: {
  params: ModelParams;
  onChange: (p: ModelParams) => void;
}) {
  const [open, setOpen] = useState(false);
  const { uom } = useOrgSettings();

  const set = <K extends keyof ModelParams>(k: K, v: ModelParams[K]) =>
    onChange({ ...params, [k]: v });

  return (
    <Card>
      <div className="p-5">
        <button type="button" onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 w-full">
          <Calculator className="w-4 h-4 text-blue-500" />
          NPV Model Parameters
          {open ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
        </button>
        {open && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Target crop</label>
              <input type="text" value={params.targetCrop} onChange={e => set('targetCrop', e.target.value)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Price ({uom.currency_symbol}/kg)</label>
              <input type="number" min={0} value={params.pricePerKg} onChange={e => set('pricePerKg', parseFloat(e.target.value) || 0)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Yield (kg/ha)</label>
              <input type="number" min={0} value={params.kgPerHa} onChange={e => set('kgPerHa', parseFloat(e.target.value) || 0)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Capex ({uom.currency_symbol}/ha)</label>
              <input type="number" min={0} value={params.capexPerHa} onChange={e => set('capexPerHa', parseFloat(e.target.value) || 0)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Opex/yr ({uom.currency_symbol}/ha)</label>
              <input type="number" min={0} value={params.opexPerHa} onChange={e => set('opexPerHa', parseFloat(e.target.value) || 0)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Discount rate (%)</label>
              <input type="number" min={0} max={30} step={0.5} value={params.discountRate} onChange={e => set('discountRate', parseFloat(e.target.value) || 0)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Horizon (years)</label>
              <input type="number" min={1} max={30} value={params.horizonYears} onChange={e => set('horizonYears', parseInt(e.target.value) || 10)}
                className="w-full text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white" />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

const DEFAULT_PARAMS: ModelParams = {
  targetCrop: 'Kiwi',
  pricePerKg: 2.5,
  kgPerHa: 15000,
  capexPerHa: 8000,
  opexPerHa: 2000,
  discountRate: 8,
  horizonYears: 10,
};

export const LandExpansionPage: React.FC = () => {
  // Raw satellite indices — fetched once and cached; not affected by NPV params.
  const [siteIndices, setSiteIndices] = useState<SiteIndices[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [params, setParams]           = useState<ModelParams>(DEFAULT_PARAMS);
  const { uom }                       = useOrgSettings();

  // Derive NPV on the fly from cached index data — no API call needed.
  const sites: SiteScore[] = siteIndices.map(s => {
    const { npv, paybackYears } = calcNpv(s.field.area_hectares ?? 1, s.ndviMean, params);
    return { ...s, npvProxy: npv, paybackYears };
  });

  const load = useCallback(async (bust = false) => {
    try {
      setLoading(true);
      setError(null);
      if (bust) invalidateCache();

      // Boundaries and imagery via shared TTL cache — rate-limit safe
      const fields = await getBoundaries();
      const imageryMap = await getBatchedImagery(fields, 50);

      const results: SiteIndices[] = fields.map(field => {
        const records = imageryMap.get(field.id) ?? [];
        let ndviMean: number | null = null;
        let ndreMean: number | null = null;
        let ndwiMean: number | null = null;

        if (records.length > 0) {
          const sums: Record<string, { s: number; c: number }> = {};
          records.forEach(r => (r.indices ?? []).forEach((idx: any) => {
            if (!sums[idx.index_type]) sums[idx.index_type] = { s: 0, c: 0 };
            sums[idx.index_type].s += idx.mean_value;
            sums[idx.index_type].c += 1;
          }));
          if (sums['NDVI']) ndviMean = +(sums['NDVI'].s / sums['NDVI'].c).toFixed(4);
          if (sums['NDRE']) ndreMean = +(sums['NDRE'].s / sums['NDRE'].c).toFixed(4);
          if (sums['NDWI']) ndwiMean = +(sums['NDWI'].s / sums['NDWI'].c).toFixed(4);
        }

        const { score, tier } = computeSuitability(ndviMean, ndreMean, ndwiMean);
        return { field, ndviMean, ndreMean, ndwiMean, suitabilityScore: score, suitabilityTier: tier, expanded: false };
      });

      results.sort((a, b) => b.suitabilityScore - a.suitabilityScore);
      setSiteIndices(results);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load site data');
    } finally {
      setLoading(false);
    }
  }, []);   // no params dependency — NPV is derived client-side

  useEffect(() => { load(); }, [load]);

  const toggle = (idx: number) =>
    setSiteIndices(prev => prev.map((s, i) => i === idx ? { ...s, expanded: !s.expanded } : s));

  const totalPositiveNpv = sites.filter(s => s.npvProxy !== null && s.npvProxy > 0).length;

  return (
    <div className="space-y-6">

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Expand className="w-7 h-7 text-blue-600" /> Land Expansion Modelling
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Site suitability scoring and NPV proxy analysis for new crop land acquisitions
          </p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Model params */}
      <ParamsPanel params={params} onChange={setParams} />

      {/* Summary */}
      {!loading && sites.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Sites Analysed',       value: sites.length, colour: 'text-gray-700 dark:text-gray-300' },
            { label: 'Excellent / Good',      value: sites.filter(s => s.suitabilityTier === 'excellent' || s.suitabilityTier === 'good').length, colour: 'text-green-600' },
            { label: 'Positive NPV',          value: totalPositiveNpv, colour: 'text-teal-600' },
            { label: `Best NPV (${uom.currency_symbol})`, value: sites.filter(s => s.npvProxy !== null).length > 0 ? Math.max(...sites.map(s => s.npvProxy ?? -Infinity)).toLocaleString() : '—', colour: 'text-blue-600' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : sites.length === 0 ? (
        <Card className="p-10 text-center">
          <ZapOff className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No field boundaries found</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sites.map((site, idx) => (
            <SiteCard key={site.field.id} site={site} onToggle={() => toggle(idx)} params={params} />
          ))}
        </div>
      )}

      {/* Disclaimer */}
      <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 p-4 flex gap-3">
        <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
          <strong>Disclaimer:</strong> NPV proxy is a directional screening tool only. It does not account for
          soil laboratory data, regulatory approval timelines, land title costs, infrastructure, or climate risk.
          All parameters are editable — adjust to match actual feasibility study assumptions.
        </p>
      </div>
    </div>
  );
};

export default LandExpansionPage;
// Made with Bob
